/**
 * Sheets sync state (ARD 9.4 and 9.6). The API marks a row with `_sync` while
 * it has a change that did not land yet. This module holds the shapes, the
 * reason table, and the pure helpers that turn a change into a marker.
 */

export type SyncChangeStatus = "PENDING" | "SYNCING" | "SYNCED" | "FAILED";

export type SyncChangeOp = "UPDATE" | "CREATE" | "DELETE";

/**
 * What the API puts on a list row (ARD 9.4 "read your own writes"). Mirrors
 * the `_sync` object that `SheetsWriteQueueService.sync()` builds in
 * onlydash-api. The two error fields are there only when the change has an
 * error.
 */
export type RowSync = {
  changeId: string;
  status: SyncChangeStatus;
  op?: SyncChangeOp;
  lastErrorCode?: string | null;
  lastErrorMessage?: string | null;
};

/** `GET /sync/:datasourceId/status` (ARD 9.6). */
export type SyncStatus = {
  lastSyncedAt: string | null;
  pending: number;
  syncing: number;
  failed: number;
  oldestPendingAt?: string | null;
  pausedUntil?: string | null;
};

/** One item from `GET /sync/:datasourceId/changes`. */
export type SyncChange = {
  id: string;
  op: SyncChangeOp;
  collection: string;
  sheetId: number;
  rowNumber: number | null;
  columns?: string[];
  status: SyncChangeStatus;
  attempts: number;
  nextAttemptAt: string;
  /**
   * The client sets this. The list asks for `PENDING,SYNCING,FAILED`, so a
   * landed item leaves the answer and the client marks the time it left
   * (`withSettledChanges`). It drives the 3 s "Synced" marker (ARD 9.4).
   */
  syncedAt?: string | null;
  lastErrorCode: string | null;
  lastErrorMessage: string | null;
  createdAt: string;
};

/**
 * One entry in the `changes` array of a 202 write answer (ARD 9.4 /
 * `SheetsQueuedResponse` on the API).
 */
export type SheetsQueuedChange = {
  id: string;
  status: SyncChangeStatus;
  rowNumber: number | null;
  op: SyncChangeOp;
};

/**
 * HTTP 202 body when `SHEETS_WRITE_QUEUE=on` (API `SheetsQueuedResponse`).
 * When the flag is off the same routes still answer 200/201 with the old
 * synchronous shapes (a single row or void), so callers must branch on status.
 */
export type SheetsQueuedResponse = {
  changes: SheetsQueuedChange[];
  items: Array<Record<string, unknown> & { _sync?: RowSync }>;
};

export type SyncMarkerKind =
  | "pending"
  | "syncing"
  | "synced"
  | "retrying"
  | "needs_action";

export type SyncReasonAction = "retry" | "open" | "none";

export type SyncReason = {
  message: string;
  action: SyncReasonAction;
};

/** Draft copy from the ARD reason table, kept in Strict ASD-STE100. */
export const SYNC_REASONS: Record<string, SyncReason> = {
  rate_limited: {
    message: "Google Sheets is busy. OnlyDash will try again.",
    action: "none",
  },
  google_5xx: {
    message: "Google Sheets is busy. OnlyDash will try again.",
    action: "none",
  },
  network: {
    message: "Google Sheets is busy. OnlyDash will try again.",
    action: "none",
  },
  google_unavailable: {
    message: "Google Sheets did not accept this change for 24 hours.",
    action: "retry",
  },
  row_changed: {
    message:
      "This row changed in Google Sheets. OnlyDash did not save your change.",
    action: "open",
  },
  formula_cell: {
    message: "This cell has a formula. You cannot edit it in OnlyDash.",
    action: "open",
  },
  row_missing: {
    message: "This row is not in the sheet.",
    action: "open",
  },
  tab_missing: {
    message: "This tab is not in the sheet.",
    action: "open",
  },
  earlier_change_failed: {
    message: "An earlier change to this row failed.",
    action: "open",
  },
  google_file_access_lost: {
    message:
      "OnlyDash cannot open this sheet. Make sure that you have access to it.",
    action: "retry",
  },
  google_reconnect_required: {
    message: "Connect your Google account again.",
    action: "retry",
  },
};

/** Codes that retry without a user action (ARD 9.6). */
const RETRY_CODES = new Set(["rate_limited", "google_5xx", "network"]);

export const reasonFor = (code: string | null | undefined): SyncReason => {
  if (code && SYNC_REASONS[code]) return SYNC_REASONS[code];
  return { message: "OnlyDash did not save this change.", action: "retry" };
};

export const isRetryingCode = (code: string | null | undefined): boolean =>
  !!code && RETRY_CODES.has(code);

/** Whole seconds left before the next try. Never negative. */
export const secondsUntil = (
  nextAttemptAt: string | null | undefined,
  now: number
): number => {
  if (!nextAttemptAt) return 0;
  const at = Date.parse(nextAttemptAt);
  if (!Number.isFinite(at)) return 0;
  return Math.max(0, Math.ceil((at - now) / 1000));
};

/** Local `HH:MM`, or null when the value is missing or unreadable. */
export const clockTime = (
  iso: string | null | undefined,
  now: Date = new Date()
): string | null => {
  if (!iso) return null;
  const at = new Date(iso);
  if (!Number.isFinite(at.getTime())) return null;
  const base = Number.isFinite(now.getTime()) ? now : at;
  const sameDay =
    at.getFullYear() === base.getFullYear() &&
    at.getMonth() === base.getMonth() &&
    at.getDate() === base.getDate();
  const hh = String(at.getHours()).padStart(2, "0");
  const mm = String(at.getMinutes()).padStart(2, "0");
  return sameDay ? `${hh}:${mm}` : `${at.getMonth() + 1}/${at.getDate()} ${hh}:${mm}`;
};

/** "1 pending change" / "3 pending changes"; null at 0. */
export const pendingLabel = (count: number): string | null => {
  if (!count || count <= 0) return null;
  return count === 1 ? "1 pending change" : `${count} pending changes`;
};

/** Deep link to the row in Google Sheets (ARD 9.6). */
export const sheetsRowUrl = (
  spreadsheetId: string,
  sheetId: number,
  rowNumber: number
): string => {
  const a1 = `A${rowNumber}`;
  return `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit#gid=${sheetId}&range=${a1}`;
};

/**
 * The marker a row shows. `change` is the latest queued change for the row;
 * `null` means the API said nothing, so the row shows no marker. A `SYNCED`
 * change shows "Synced" for 3 s after `syncedAt`, then hides.
 */
export const markerFor = (
  change: SyncChange | null,
  now: number
): SyncMarkerKind | null => {
  if (!change) return null;
  switch (change.status) {
    case "PENDING":
      return isRetryingCode(change.lastErrorCode) && change.attempts > 0
        ? "retrying"
        : "pending";
    case "SYNCING":
      return "syncing";
    case "SYNCED":
      return "synced";
    case "FAILED":
      return "needs_action";
    default:
      return null;
  }
};

export const SYNCED_MARKER_MS = 3000;

/**
 * The 3 s "Synced" window in the real flow (ARD 9.4 and 9.6).
 *
 * The changes list asks for `PENDING,SYNCING,FAILED`, so an item that lands
 * leaves the answer. A tracked item that is now gone (or came back `SYNCED`)
 * becomes a `SYNCED` change with `syncedAt` set, and `markerFor` shows it for
 * 3 s. When the API returns `syncedAt` on a `SYNCED` item, use that value.
 * Otherwise use the time the item left the list. Then drop the item, so the
 * marker hides after the window.
 */
export const withSettledChanges = (
  previous: SyncChange[] | undefined,
  next: SyncChange[],
  now: number
): SyncChange[] => {
  if (!previous) return next;

  const previousById = new Map(previous.map((change) => [change.id, change]));
  const settledAt = new Date(now).toISOString();
  const seen = new Set<string>();

  const merged = next.map((change) => {
    seen.add(change.id);
    const before = previousById.get(change.id);
    // A SYNCED item without `syncedAt` keeps the time the previous answer gave
    // it, or takes the current time.
    if (change.status === "SYNCED" && !change.syncedAt) {
      return { ...change, syncedAt: before?.syncedAt ?? settledAt };
    }
    return change;
  });

  previous.forEach((old) => {
    if (seen.has(old.id)) return;
    seen.add(old.id);
    // Gone from the list between two answers: it landed. (A dismissed item
    // also leaves the list; "Synced" for 3 s is a harmless over-report there.)
    // An item that was already settled is not settled again.
    if (old.status !== "PENDING" && old.status !== "SYNCING") return;
    merged.push({ ...old, status: "SYNCED", syncedAt: settledAt });
  });

  return merged;
};

/** Text for a marker, or null when the marker shows no text. */
export const markerLabel = (
  kind: SyncMarkerKind,
  change: SyncChange | null,
  now: number
): string => {
  switch (kind) {
    case "pending":
      return "Pending";
    case "syncing":
      return "Syncing";
    case "synced":
      return "Synced";
    case "retrying":
      return `Failed: retrying in ${secondsUntil(change?.nextAttemptAt, now)}s`;
    case "needs_action":
      return "Failed: needs action";
    default:
      return "";
  }
};

export const isFailedMarker = (kind: SyncMarkerKind | null): boolean =>
  kind === "needs_action";
